import React, {useMemo} from 'react';
import {View, Text} from 'react-native';
import {FilterTypeConfig, StatusIndicatorProps} from './StatusIndicatorProps';
import {createStatusIndicatorStyles} from './StatusIndicator.styles';
import PartsAssignedIcon from '@shared/assets/icons/PartsAssignedIcon';

/**
 * Central mapping — drives style resolution, icon, and icon colour.
 *
 *  'priority'        → styles.priorityMedium,  no icon
 *  'partsAssigned'   → styles.partsAssigned,           PartsAssignedIcon (enabled colour)
 *  'partsUnAssigned' → styles.partsUnAssigned,         PartsAssignedIcon (disabled colour)
 */
const FILTER_TYPE_CONFIG: Record<string, FilterTypeConfig> = {
  priority: {
    styleMappingRequired: false,
    defaultStyleKey: 'priorityMedium',
  },
  partsAssigned: {
    styleMappingRequired: true,
    Icon: PartsAssignedIcon,
    getIconColor: (theme: any) => theme.partsAssigneEnabledIcon,
  },
  partsUnAssigned: {
    styleMappingRequired: true,
    Icon: PartsAssignedIcon,
    getIconColor: (theme: any) => theme.partsAssignedDisabledIcon,
  },
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  filterType,
  theme,
  title,
  containerStyle,
  testID,
  stylesRequired = true,
}) => {
  const styles = useMemo(() => createStatusIndicatorStyles(theme), [theme]);
  const styleMap = styles as Record<string, any>;

  const config = FILTER_TYPE_CONFIG[filterType];
  const Icon = config?.Icon;
  const iconColor = config?.getIconColor ? config.getIconColor(theme) : undefined;

  /**
   * Style resolution
   *
   *  stylesRequired=false                                → noStyle (no border, no bg)
   *  config.styleMappingRequired=true  (partsAssigned)  → styles[filterType] Ex styles[partsAssigned], styles[partsUnassigned]
   *  config.styleMappingRequired=false (priority etc...)       → styles[defaultStyleKey] Ex: styles[priority], styles[partsHistory]
   *  unknown filterType                                 → {}
   */
  const resolvedStyle = useMemo(() => {
    if (!stylesRequired) {
      return styles.noStyle;
    }
    if (!config) {
      return {};
    }
    if (config.styleMappingRequired) {
      return styleMap[filterType] ?? {};
    }
    return styleMap[config.defaultStyleKey ?? ''] ?? {};
  }, [stylesRequired, config, filterType, styleMap, styles.noStyle]);

  return (
    <View
      style={[styles.badgeContainer, resolvedStyle, containerStyle]}
      testID={testID ?? `statusIndicator-${filterType}`}
      accessible={true}
      accessibilityRole="text"
      accessibilityLabel={title}>
      {Icon && <Icon color={iconColor} />}
      <Text style={styles.badgeText}>{title?.toLocaleUpperCase()}</Text>
    </View>
  );
};

export default StatusIndicator;